import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { format } from 'date-fns';
import {
  combineLatest, Observable, switchMap, take
} from 'rxjs';
import { CommunicationPreference } from '@sondermind/utilities/models-clinical';
import { SegmentService } from '@sondermind/segment';
import { RecaptchaService } from '@sondermind/recaptcha';
import { FlowsLaunchDarklyService, LAUNCH_DARKLY_SERVICE } from '@sondermind/launch-darkly';
import { ReferralSourceEnum } from '@sondermind/data-access/client-referral';
import { IQuickIntakeApiContract } from '../models/quick-intake-api-contract.model';
import { QuickIntakeFormValues } from '../models/quick-intake-form.model';
import { QuickIntakeHttpService } from '../services/quick-intake.httpservice';
import { IQuickIntakeSegmentSchema } from '../models/segment/quick-intake-segment-schema.interface';

@Injectable({
  providedIn: 'root'
})
export class QuickIntakeStore {
  constructor(
    private segmentService: SegmentService<IQuickIntakeSegmentSchema>,
    private recaptchaService: RecaptchaService,
    private cookieService: CookieService,
    private quickIntakeHttpService: QuickIntakeHttpService,
    @Inject(LAUNCH_DARKLY_SERVICE)
    private launchDarklyService: FlowsLaunchDarklyService,
    // eslint-disable-next-line @typescript-eslint/ban-types
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  submitIntake$(
    formValues: QuickIntakeFormValues
  ): Observable<number | null> {
    return combineLatest({
      segmentAnonymousId: this.segmentService.anonymousId(),
      recaptchaToken: this.recaptchaService.executeEvent$('submit')
    }).pipe(
      take(1),
      switchMap((results) => {
        const body = this.buildApiRequestData(
          formValues,
          results.segmentAnonymousId ?? null,
          results.recaptchaToken
        );
        return this.quickIntakeHttpService.submitQuickIntake$(body);
      })
    );
  }

  private buildApiRequestData(
    formValues: QuickIntakeFormValues,
    segmentAnonymousId: string | null,
    recaptchaToken: string | null
  ): IQuickIntakeApiContract {
    return {
      firstName: formValues.firstName as string,
      lastName: formValues.lastName as string,
      email: formValues.email as string,
      phone: formValues.phone as string,
      receivedDate: new Date().toISOString(),
      sourceAttributes: {
        source: ReferralSourceEnum.DIRECT_REFERRAL,
        birthdate: format(new Date((formValues.birthdate as string)), 'yyyy-MM-dd'),
        location: {
          name: formValues.location.name as string,
          state: formValues.location.state as string,
          zip: formValues.location.zip,
          lat: formValues.location.lat as number,
          lng: formValues.location.lng as number
        },
        commPrefs: this.getCommPrefsArray(formValues.commPrefs),
        contactSmsAllowed: formValues.contactSmsAllowed as boolean,
        insurance: formValues.insurance as number,
        sessionFormatPreference: formValues.sessionFormatPreference,
        genderPreference: formValues.genderPreference,
        openPreferences: formValues.openPreferences as string,
        segmentAnonymousId,
        experimentationKey: this.launchDarklyService.uuid,
        externalReferral: this.getExternalReferral(),
        externalReferralSearch: this.getExternalReferralSearch(),
        medicationManagement: formValues.medicationManagement as boolean,
        readiness: formValues.readiness as string
      },
      recaptchaToken
    };
  }

  private getCommPrefsArray(commPrefs: {
    commPrefsPhone: boolean | null;
    commPrefsSms: boolean | null;
  }): string[] {
    const prefs: CommunicationPreference[] = [CommunicationPreference.NCC];
    if (commPrefs.commPrefsPhone) prefs.push(CommunicationPreference.PHONE);
    if (commPrefs.commPrefsSms) prefs.push(CommunicationPreference.SMS);
    return prefs;
  }

  private getExternalReferral(): string | undefined {
    return this.cookieService.get('EXTERNAL_REFERRAL') || undefined;
  }

  private getExternalReferralSearch(): string | undefined {
    return this.cookieService.get('EXTERNAL_REFERRAL_SEARCH') || undefined;
  }
}
